import { NgTemplateOutlet } from '@angular/common';

import { Component, OnInit, ChangeDetectorRef, forwardRef, inject, input, linkedSignal, output } from '@angular/core';
import { TranslateDirective, TranslatePipe as NgxTranslatePipe } from '@ngx-translate/core';
import { DividerComponent } from '../../../../components/panel/divider.component';
import { IconComponent } from '../../../../components/icon/icon.component';
import { SideSheetSelectorComponent } from '../../../../components/side-sheet-selector/side-sheet-selector.component';
import { SideSheetSelectorItemComponent } from '../../../../components/side-sheet-selector/side-sheet-selector-item.component';
import { SideSheetSelectorToggleStaticDirective } from '../../../../components/side-sheet-selector/side-sheet-selector-toggle-static.directive';
import { TranslatePipe } from '../../../../pipes/translate.pipe';
import { WatchlistService } from '../../../../services/watchlist/watchlist.service';
import { WatchlistUtils } from '../../../../services/watchlist/watchlist-utils';
import { SecuritySymbol } from '../../../../domain/symbol.domain';
import { Watchlist } from '../../../../domain/watchlist.domain';
import { AddToWatchlist } from '../domain';

export type AddToWatchlistType = 'slide' | 'list';

@Component({
  selector: 'td-wb-add-to-watchlist-slide',
  templateUrl: 'slide-add-to-watchlist.component.html',
  styleUrls: ['slide-add-to-watchlist.component.scss'],
  providers: [
    { provide: AddToWatchlist, useExisting: forwardRef(() => SlideAddToWatchlistComponent) }
  ],
  imports: [
    NgTemplateOutlet,
    DividerComponent,
    IconComponent,
    TranslateDirective,
    NgxTranslatePipe,
    SideSheetSelectorComponent,
    TranslatePipe,
    SideSheetSelectorItemComponent,
    SideSheetSelectorToggleStaticDirective
  ]
})
export class SlideAddToWatchlistComponent implements OnInit, AddToWatchlist {
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly watchlistService = inject(WatchlistService);

  readonly watchlistsInput = input<Array<Watchlist>>([], { alias: 'watchlists' });
  readonly watchlists = linkedSignal<Array<Watchlist>>(() => this.watchlistsInput());
  readonly symbol = input<SecuritySymbol>(undefined);
  readonly type = input<AddToWatchlistType>('slide');
  readonly watchListsLoaded = input<boolean>(false);
  readonly compactView = input<boolean>(false);

  readonly symbolAdded = output<void>();

  symbolAddedCompleted: boolean = false;
  symbolAddedFailed: boolean = false;
  menuOpened: boolean = false;

  ngOnInit(): void {
    if (!this.watchListsLoaded()) {
      this.watchlistService.getAllWatchlists(false).subscribe((watchlists: Array<Watchlist>): void => {
        this.watchlists.set(watchlists);
        this.changeDetectorRef.markForCheck();
      });
    }
  }

  /**
   * Answers true if a given Watchlist is at max capacity, false otherwise.
   */
  isFull(watchlist: Watchlist): boolean {
    return WatchlistUtils.isWatchlistAtMaxCapacity(watchlist);
  }

  /**
   * Answers true if a given Watchlist contains the selected symbol.
   */
  hasSymbol(watchlist: Watchlist): boolean {
    return WatchlistUtils.hasSymbol(watchlist, this.symbol());
  }

  isDisabled(watchlist: Watchlist): boolean {
    return this.isFull(watchlist) || this.hasSymbol(watchlist);
  }

  /**
   * Add the selected symbol to a given Watchlist.
   */
  addSymbolToWatchlist(watchlist: Watchlist): void {
    if (!this.isFull(watchlist) && !this.hasSymbol(watchlist)) {
      WatchlistUtils.addToWatchlist(watchlist, this.symbol());
      this.watchlistService.saveWatchlist(watchlist).subscribe({
        next: (): void => {
          this.symbolAddedCompleted = true;
          this.symbolAdded.emit(undefined);
        },
        error: (): void => {
          this.symbolAddedFailed = true;
        },
        complete: (): void => {
          this.addSymbolToWatchlistComplete();
          this.changeDetectorRef.markForCheck();
        }
      });
    }
  }

  trackByWatchlistNumber(_: number, item: Watchlist): number {
    return item.number;
  }

  setMenuToggle(): void {
    this.menuOpened = !this.menuOpened;
  }

  private addSymbolToWatchlistComplete(): void {
    setTimeout((): void => {
      this.symbolAddedCompleted = false;
      this.symbolAddedFailed = false;
      this.changeDetectorRef.markForCheck();
    }, 3000);
  }
}
